import { React, useEffect, useState } from "react";
import "./ManageBook.css";
import AntTable from "./Table";
import Loading from "./Loading";
import { db } from "../Firebase/FirebaseConnection";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { Modal, Form, Button, Input as AntInput, message, Select } from "antd";
import { addBook } from "./BookService";
import BookSearchSelect from "../BookSearchSelect ";
import TextArea from "antd/es/input/TextArea";
import SearchBar from "./Search";

const ManageBook = () => {
  const [isLoading, setLoading] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [refreshBooks, setRefreshBooks] = useState(() => () => {});
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [existingBookDoc, setExistingBookDoc] = useState(null);
  const [isConfirmModalVisible, setConfirmModalVisible] = useState(false);
  const [isSelectTypeModalVisible, setSelectTypeModalVisible] = useState(false);
  const [isAddNewModalVisible, setAddNewModalVisible] = useState(false);
  const [isAddExistingModalVisible, setAddExistingModalVisible] =
    useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const booksSnapshot = await getDocs(collection(db, "books"));
        const departmentsSet = new Set();
        const categoriesSet = new Set();
        booksSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.department) {
            if (Array.isArray(data.department)) {
              data.department.forEach((dept) => {
                departmentsSet.add(dept);
              });
            } else {
              departmentsSet.add(data.department);
            }
          }
          if (data.category) {
            categoriesSet.add(data.category);
          }
        });
        setDepartmentOptions(Array.from(departmentsSet));
        setCategoryOptions(Array.from(categoriesSet));
      } catch (error) {
        console.error("Error fetching departments and categories:", error);
      }
    };

    fetchOptions();
  }, []);

  const showAddModal = () => {
    setSelectTypeModalVisible(true);
  };

  const logActivity = async (
    bookTitle,
    oldStatus,
    newStatus,
    fineAmount,
    actionType,
    author,
    isbn
  ) => {
    try {
      const userName = localStorage.getItem("userName");
      const logRef = collection(db, "user-activity-logs");

      const now = new Date();
      const formattedDateTime = now.toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });

      await addDoc(logRef, {
        bookTitle: bookTitle,
        oldStatus: oldStatus,
        newStatus: newStatus,
        fineAmount: fineAmount,
        updatedBy: userName,
        timestamp: now,
        formattedDateTime,
        actionType: actionType,
        author: author,
        isbn: isbn,
        d: isbn,
      });

      console.log(
        `Activity logged: ${bookTitle} status changed from ${oldStatus} to ${newStatus} with a fine of ₱${fineAmount}.`
      );
    } catch (error) {
      console.error("Failed to log activity:", error);
    }
  };

  const checkDuplicateBook = async (isbn, title, author) => {
    const booksRef = collection(db, "books");
    const snapshot = await getDocs(booksRef);

    return snapshot.docs.find((doc) => {
      const data = doc.data();
      return (
        data.isbn === isbn &&
        data.title.trim().toLowerCase() === title.trim().toLowerCase() &&
        data.author.trim().toLowerCase() === author.trim().toLowerCase()
      );
    });
  };

  const handleAddBook = async (values) => {
    setLoading(true);

    const duplicate = await checkDuplicateBook(
      values.isbn,
      values.title,
      values.author
    );

    if (duplicate) {
      setExistingBookDoc(duplicate);
      setLoading(false);
      setIsAddModalVisible(false);
      setConfirmModalVisible(true);
      return;
    }

    await addNewBook(values);
  };

  const addNewBook = async (values) => {
    setAddNewModalVisible(false);
    setLoading(true);
    try {
      const bookData = {
        ...values,
        addedDate: new Date().toISOString(),
      };
      await addBook(bookData);
      await logActivity(
        values.title,
        "None",
        "Added",
        0,
        "add",
        values.author,
        values.isbn
      );
      message.success("Book added successfully!");
      refreshBooks();
    } catch (err) {
      message.error("Failed to add book.");
    } finally {
      form.resetFields();
      setLoading(false);
    }
  };

  return (
    <div className="manageBook-main-container">
      <div className="table-container">
        {isLoading && (
          <div className="loading-overlay">
            <Loading />
            <span>Please Wait, Saving....</span>
          </div>
        )}

        <SearchBar onClick={showAddModal} showAddButton={true} />

        <AntTable setRefreshBooks={setRefreshBooks} />
      </div>

      <Modal
        className="custom-modal"
        title="Add New Book"
        visible={isAddNewModalVisible}
        onCancel={() => {
          if (!isLoading) setAddNewModalVisible(false);
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ bookStatus: "Good" }}
          onFinish={handleAddBook}
          onFinishFailed={(errorInfo) => {
            message.error(
              "Please complete all required fields before adding a book."
            );
            setAddExistingModalVisible(false);
            setExistingBookDoc(null);
          }}
        >
          <Form.Item
            name="title"
            label="Book Title"
            rules={[
              { required: true, message: "Please input the book title!" },
            ]}
          >
            <AntInput />
          </Form.Item>

          <Form.Item
            name="author"
            label="Book Author"
            rules={[
              {
                required: true,
                message: "Please input the author's name!",
              },
            ]}
          >
            <AntInput />
          </Form.Item>

          <Form.Item
            name="category"
            label="Book Category"
            rules={[
              { required: true, message: "Please select or add the category!" },
            ]}
          >
            <Select
              allowClear
              className="dept-option"
              mode="tags"
              placeholder="Select or Input for New Category"
            >
              {categoryOptions.map((cat) => (
                <Select.Option key={cat} value={cat}>
                  {cat}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="isbn"
            label="Book ISBN"
            rules={[{ required: true, message: "Please input the ISBN!" }]}
          >
            <AntInput />
          </Form.Item>

          <Form.Item
            name="publisher"
            label=" Book Publisher"
            rules={[{ required: true, message: "Please input the publisher!" }]}
          >
            <AntInput />
          </Form.Item>

          <Form.Item
            name="year"
            label="Book Year"
            rules={[{ required: true, message: "Please input the year!" }]}
          >
            <AntInput />
          </Form.Item>
          <Form.Item
            name="bookStatus"
            label="Book Status"
            rules={[
              { required: true, message: "Please select the book status!" },
            ]}
          >
            <Select placeholder="Select Book Status" className="dept-option">
              <Select.Option value="Good">Good</Select.Option>
              <Select.Option value="Damaged">Damaged</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="copiesAvailable"
            label="Book Copies Available"
            rules={[
              {
                required: true,
                message: "Please input the available copies!",
              },
            ]}
          >
            <AntInput />
          </Form.Item>

          <Form.Item
            name="totalCopies"
            label="Book Total Copies"
            rules={[
              { required: true, message: "Please input the total copies!" },
            ]}
          >
            <AntInput />
          </Form.Item>

          <Form.Item
            name="shelfLocation"
            label="Book Shelf Number"
            rules={[
              {
                required: true,
                message: "Please input the shelf Number!",
              },
            ]}
          >
            <AntInput />
          </Form.Item>
          <Form.Item
            name="layerNumber"
            label="Book Shelf Layer Number"
            rules={[
              {
                required: true,
                message: "Please input the Book Shelf Layer Number!",
              },
            ]}
          >
            <AntInput />
          </Form.Item>

          <Form.Item
            name="department"
            label="Book Department/Strand field"
            rules={[
              {
                required: true,
                message: "Please select at least one department!",
              },
            ]}
          >
            <Select
              allowClear
              className="dept-option"
              mode="tags"
              placeholder="Select or Input for New Department/Strand field"
            >
              {departmentOptions.map((dept) => (
                <Select.Option key={dept} value={dept}>
                  {dept}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="bookDescription"
            label="Book Description"
            rules={[
              {
                required: true,
                message: "Please input the book description!",
              },
            ]}
          >
            <TextArea />
          </Form.Item>

          <Button type="primary" htmlType="submit" style={{ width: "100%" }}>
            Add Book
          </Button>
        </Form>
      </Modal>
      <Modal
        visible={isConfirmModalVisible}
        title="Book Already Exists"
        onCancel={() => {
          setConfirmModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          onFinish={async (values) => {
            try {
              const data = existingBookDoc.data();
              const docRef = doc(db, "books", existingBookDoc.id);

              const additionalCopies = parseInt(values.totalCopies || "0");
              const updatedAvailable =
                parseInt(data.copiesAvailable || "0") + additionalCopies;
              const updatedTotal =
                parseInt(data.totalCopies || "0") + additionalCopies;

              await updateDoc(docRef, {
                copiesAvailable: String(updatedAvailable),
                totalCopies: String(updatedTotal),
              });

              await logActivity(
                data.title,
                "Existing",
                "Copies Updated",
                0,
                "update_copies",
                data.author,
                data.isbn
              );

              message.success("Book copies updated.");
              setConfirmModalVisible(false);
              refreshBooks();
            } catch (err) {
              message.error("Failed to update copies.");
            } finally {
              setLoading(false);
            }
          }}
          layout="vertical"
        >
          <p>This book already exists. Would you like to add more copies?</p>

          <Form.Item
            name="totalCopies"
            label="Additional Total Copies"
            rules={[{ required: true, message: "Please input total copies." }]}
          >
            <AntInput />
          </Form.Item>

          <Button type="primary" htmlType="submit" block>
            Add Copies
          </Button>
        </Form>
      </Modal>
      <Modal
        title="Add Book"
        visible={isSelectTypeModalVisible}
        onCancel={() => setSelectTypeModalVisible(false)}
        footer={null}
      >
        <Button
          type="primary"
          block
          onClick={() => {
            setSelectTypeModalVisible(false);
            setAddExistingModalVisible(true);
          }}
        >
          Add Existing Book
        </Button>

        <Button
          style={{ marginTop: 10 }}
          block
          onClick={() => {
            setSelectTypeModalVisible(false);
            setAddNewModalVisible(true);
          }}
        >
          Add New Book
        </Button>
      </Modal>
      <Modal
        title="Add Copies to Existing Book"
        visible={isAddExistingModalVisible}
        onCancel={() => {
          setAddExistingModalVisible(false);
          setExistingBookDoc(null); // reset selected book
        }}
        footer={null}
      >
        {/* Replace SearchBar with your actual search logic */}
        <BookSearchSelect
          onSelectBook={(bookDoc) => setExistingBookDoc(bookDoc)}
        />

        {existingBookDoc && (
          <>
            <Form
              onFinish={async (values) => {
                try {
                  const data = existingBookDoc.data();
                  const docRef = doc(db, "books", existingBookDoc.id);

                  const additionalCopies = parseInt(values.totalCopies || "0");
                  const updatedAvailable =
                    parseInt(data.copiesAvailable || "0") + additionalCopies;
                  const updatedTotal =
                    parseInt(data.totalCopies || "0") + additionalCopies;

                  await updateDoc(docRef, {
                    copiesAvailable: String(updatedAvailable),
                    totalCopies: String(updatedTotal),
                  });

                  await logActivity(
                    data.title,
                    "Existing",
                    "Copies Updated",
                    0,
                    "update_copies",
                    data.author,
                    data.isbn
                  );

                  message.success("Book copies updated.");
                  setAddExistingModalVisible(false);
                  refreshBooks();
                  setExistingBookDoc(null);
                } catch (err) {
                  message.error("Failed to update copies.");
                } finally {
                  setLoading(false);
                }
              }}
              layout="vertical"
            >
              <Form.Item
                name="totalCopies"
                label="Additional Copies"
                rules={[
                  { required: true, message: "Please input number of copies." },
                ]}
              >
                <AntInput />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                block
                loading={isLoading}
              >
                Add Copies
              </Button>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ManageBook;
